import React, { useState, useEffect } from 'react';
import { RefreshCw, MessageCircle, Swords, FlaskConical } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import ModelLogo, { getCategoryIcon, Icons } from './ModelLogo';
import { trackEvent } from '../utils/GoogleAnalytics';
import { CACHE_DURATIONS } from '../config/cacheConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// CACHE CONFIGURATION
// ==========================================
const CACHE_DURATION = CACHE_DURATIONS.ANALYTICS;
const analyticsCache = {
  data: {},      // { week: {...}, month: {...}, all: {...} }
  timestamp: {}  // { week: 123456, month: 123456, all: 123456 }
};

const CHART_PALETTES = {
  purple: ['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE', '#F5F3FF'],
  blue: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE', '#EFF6FF'],
  green: ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#D1FAE5', '#ECFDF5'],
  amber: ['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7', '#FFFBEB'],
  rose: ['#F43F5E', '#FB7185', '#FDA4AF', '#FECDD3', '#FFE4E6', '#FFF1F2'],
};

function Dashboard({ user }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');
  const [commentsCount, setCommentsCount] = useState(0);

  // Feature stats (Chat + Tone)
  const [chatStats, setChatStats] = useState({ count: 0, messages: 0 });
  const [toneStats, setToneStats] = useState({ count: 0 });

  const loadAnalytics = async (forceRefresh = false) => {
    if (!forceRefresh && analyticsCache.data[timeRange]) {
      const cacheAge = Date.now() - (analyticsCache.timestamp[timeRange] || 0);
      if (cacheAge < CACHE_DURATION) {
        setAnalytics(analyticsCache.data[timeRange]);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      // STATIC MODE: load from local JSON files
      const [analyticsRes, chatRes, toneRes] = await Promise.all([
        fetch('/data/analytics.json').catch(() => null),
        fetch('/data/chat-stats.json').catch(() => null),
        fetch('/data/tone-stats.json').catch(() => null),
      ]);

      if (analyticsRes?.ok) {
        const data = await analyticsRes.json();
        analyticsCache.data[timeRange] = data;
        analyticsCache.timestamp[timeRange] = Date.now();
        setAnalytics(data);
      }

      if (chatRes?.ok) {
        const data = await chatRes.json();
        setChatStats({
          count: data.chats?.count || 0,
          messages: data.chats?.messages || 0,
        });
      }

      if (toneRes?.ok) {
        const data = await toneRes.json();
        setToneStats({ count: data.total_runs || 0 });
      }
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  // STATIC MODE: comments count disabled
  // useEffect(() => { loadCommentsCount(); }, [timeRange]);

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    trackEvent('Analytics', 'Change Time Range', newRange);
  };

  const handleRefresh = () => {
    loadAnalytics(true);
    trackEvent('Analytics', 'Refresh Data');
  };

  const getModelsData = () => {
    if (!analytics?.top_models) return [];
    return analytics.top_models
      .slice(0, 6)
      .map((item) => ({
        id: item.model || item.name,
        name: formatModelName(item.model || item.name),
        fullName: item.model || item.name,
        value: item.count || item.usage || item.wins || 0,
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  const getVotesByModelData = () => {
    if (!analytics?.top_models) return [];
    return analytics.top_models
      .filter(item => (item.wins || item.votes || 0) > 0)
      .slice(0, 6)
      .map((item) => ({
        id: item.model || item.name,
        name: formatModelName(item.model || item.name),
        fullName: item.model || item.name,
        value: item.wins || item.votes || 0,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getCategoryData = () => {
    if (!analytics?.category_breakdown) return [];
    return Object.entries(analytics.category_breakdown)
      .map(([category, count]) => ({
        id: category,
        name: formatCategoryName(category),
        value: count,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getLatencyData = () => {
    if (!analytics?.latency_by_model) return [];
    return Object.entries(analytics.latency_by_model)
      .map(([model, latency]) => ({
        id: model,
        name: formatModelName(model),
        fullName: model,
        value: Math.round(latency / 1000 * 10) / 10,
      }))
      .filter(item => item.value > 0 && item.value < 600)
      .sort((a, b) => a.value - b.value)
      .slice(0, 8);
  };

  const getModelPairsData = () => {
    if (!analytics?.model_pairs) return [];
    return analytics.model_pairs.slice(0, 6).map((pair) => ({
      id: `${pair.model1}_${pair.model2}`,
      name: `${formatModelName(pair.model1)} vs ${formatModelName(pair.model2)}`,
      model1: pair.model1,
      model2: pair.model2,
      value: pair.count || 0,
    })).sort((a, b) => b.value - a.value);
  };

  const getStats = () => {
    const modelsData = getModelsData();
    const categoryData = getCategoryData();

    return {
      results: analytics?.total_public_results || 0,
      votes: analytics?.total_votes || 0,
      models: analytics?.unique_models || modelsData.length,
      categories: categoryData.length || 0,
    };
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
      <div className="max-w-6xl mx-auto px-0 sm:px-6 pt-3" role="status" aria-label="Loading analytics">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" aria-hidden="true"></div>
          <span className="sr-only">Loading analytics data...</span>
        </div>
      </div>
      </div>
    );
  }

  const stats = getStats();

  const getFeatureUsageData = () => {
    const data = [];
    if (chatStats.count > 0) data.push({ id: 'chat', name: 'AI Chat', value: chatStats.count });
    if (analytics?.total_public_results > 0) data.push({ id: 'compare', name: 'AI Compare', value: analytics.total_public_results });
    if (toneStats.count > 0) data.push({ id: 'tone', name: 'AI Tone', value: toneStats.count });
    return data.sort((a, b) => b.value - a.value);
  };

  const FEATURE_COLORS = ['#3B82F6', '#8B5CF6', '#F43F5E'];

  const totalActivities = chatStats.count + (analytics?.total_public_results || 0) + toneStats.count;

  return (
    <div className="px-4 sm:px-0">
    <div className="max-w-6xl mx-auto px-0 sm:px-6 pt-3 pb-8">

      {/* Header: Feature Stats + Time Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500">{totalActivities.toLocaleString()} total activities</p>
        </div>
        {/* TIME FILTER HIDDEN - portfolio demo (showing all-time data)
        <div className="flex items-center space-x-2">
          <select value={timeRange} onChange={(e) => handleTimeRangeChange(e.target.value)} ...>
            <option value="day">24 hours</option>
            <option value="week">7 days</option>
            <option value="month">30 days</option>
          </select>
          <button onClick={handleRefresh}><RefreshCw /></button>
        </div>
        */}
      </div>

      {/* Feature Overview — compact on mobile, 3 cards on desktop */}
      {/* Mobile: single card with 3 columns */}
      <motion.div
        className="sm:hidden bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="h-1 bg-gradient-to-r from-blue-400 via-violet-400 to-rose-400" />
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          {/* AI Chat */}
          <motion.div className="p-3 text-center"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}>
            <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-1.5">
              <MessageCircle size={16} className="text-blue-600" />
            </div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Chat</p>
            <p className="text-lg font-bold text-blue-700 leading-tight">{chatStats.count}</p>
            <p className="text-[10px] text-gray-400">{chatStats.messages} msgs</p>
          </motion.div>

          {/* AI Compare */}
          <motion.div className="p-3 text-center"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, duration: 0.4 }}>
            <div className="w-9 h-9 bg-violet-50 rounded-xl flex items-center justify-center mx-auto mb-1.5">
              <Swords size={16} className="text-violet-600" />
            </div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Compare</p>
            <p className="text-lg font-bold text-violet-700 leading-tight">{analytics?.total_public_results || 0}</p>
            <p className="text-[10px] text-gray-400">{analytics?.total_votes || 0} votes</p>
          </motion.div>

          {/* AI Tone */}
          <motion.div className="p-3 text-center"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}>
            <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center mx-auto mb-1.5">
              <FlaskConical size={16} className="text-rose-600" />
            </div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Tone</p>
            <p className="text-lg font-bold text-rose-700 leading-tight">{toneStats.count}</p>
            <p className="text-[10px] text-gray-400">remixes</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Desktop: 3 separate animated cards */}
      <div className="hidden sm:grid sm:grid-cols-3 gap-3 mb-6">
        {[
          {
            label: 'AI Chat', icon: MessageCircle, border: 'border-blue-200',
            gradient: 'from-blue-400 to-sky-500', iconBg: 'bg-blue-50', iconColor: 'text-blue-600',
            stats: [
              { value: chatStats.count, label: 'Chats', color: 'text-blue-700' },
              { value: chatStats.messages, label: 'Messages', color: 'text-blue-400' },
            ]
          },
          {
            label: 'AI Compare', icon: Swords, border: 'border-violet-200',
            gradient: 'from-violet-400 to-purple-500', iconBg: 'bg-violet-50', iconColor: 'text-violet-600',
            stats: [
              { value: analytics?.total_public_results || 0, label: 'Runs', color: 'text-violet-700' },
              { value: analytics?.total_votes || 0, label: 'Votes', color: 'text-violet-400' },
            ]
          },
          {
            label: 'AI Tone', icon: FlaskConical, border: 'border-rose-200',
            gradient: 'from-rose-400 to-pink-500', iconBg: 'bg-rose-50', iconColor: 'text-rose-600',
            stats: [
              { value: toneStats.count, label: 'Remixes', color: 'text-rose-700' },
            ]
          },
        ].map((card, i) => (
          <motion.div
            key={card.label}
            className={`bg-white rounded-2xl border ${card.border} shadow-sm overflow-hidden hover:shadow-md transition-shadow`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 * i, duration: 0.5, ease: 'easeOut' }}
          >
            <div className={`h-1 bg-gradient-to-r ${card.gradient}`} />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 ${card.iconBg} rounded-lg flex items-center justify-center`}>
                  <card.icon size={16} className={card.iconColor} />
                </div>
                <span className="font-bold text-gray-900 text-sm">{card.label}</span>
              </div>
              <div className="flex items-baseline gap-4">
                {card.stats.map((stat) => (
                  <div key={stat.label}>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</p>
                    <p className="text-[11px] text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="space-y-5">

        {/* Row 1: Feature Usage + Models Used */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ChartCard title="Feature Usage" subtitle="activities by feature" color="blue">
            {getFeatureUsageData().length > 0 ? (
              <FeatureUsagePie data={getFeatureUsageData()} colors={FEATURE_COLORS} />
            ) : (
              <EmptyState message="No activity yet" />
            )}
          </ChartCard>

          <ChartCard title="Models Used" subtitle="across all comparisons" color="purple">
            {getModelsData().length > 0 ? (
              <PieChartWithLegend data={getModelsData()} palette="purple" type="model" />
            ) : (
              <EmptyState />
            )}
          </ChartCard>
        </div>

        {/* Row 2: Votes + Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ChartCard title="Votes by Model" subtitle="community preference" color="green">
            {getVotesByModelData().length > 0 ? (
              <HorizontalBarChartWithLogos data={getVotesByModelData()} palette="green" unit=" votes" />
            ) : (
              <EmptyState message="No votes yet" />
            )}
          </ChartCard>

          <ChartCard title="Categories" subtitle="comparison types" color="blue">
            {getCategoryData().length > 0 ? (
              <PieChartWithLegend data={getCategoryData()} palette="blue" type="category" />
            ) : (
              <EmptyState />
            )}
          </ChartCard>
        </div>

        {/* Row 3: Latency + Model Pairs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ChartCard title="Average Latency" subtitle="response time (lower is better)" color="amber">
            {getLatencyData().length > 0 ? (
              <HorizontalBarChartWithLogos data={getLatencyData()} palette="amber" unit="s" />
            ) : (
              <EmptyState message="No latency data" />
            )}
          </ChartCard>

          <ChartCard title="Popular Matchups" subtitle="most compared pairs" color="rose">
            {getModelPairsData().length > 0 ? (
              <ModelPairsChart data={getModelPairsData()} />
            ) : (
              <EmptyState message="Compare models to see matchups" />
            )}
          </ChartCard>
        </div>
      </div>
    </div>
    </div>
  );
}

// ==========================================
// PIE CHART WITH LEGEND
// ==========================================

function PieChartWithLegend({ data, palette, type }) {
  const colors = CHART_PALETTES[palette] || CHART_PALETTES.blue;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = ((item.value / total) * 100).toFixed(1);
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2">
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-600">{item.value} ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 h-full">
      <div className="w-full sm:w-1/2 h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full sm:w-1/2 space-y-2">
        {data.map((item, index) => {
          const percentage = ((item.value / total) * 100).toFixed(0);
          return (
            <div key={item.id} className="flex items-center gap-2">
              <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
                {type === 'model' ? (
                  <ModelLogo modelName={item.fullName || item.id} size="xs" />
                ) : (
                  getCategoryIcon(item.id, 16)
                )}
              </div>
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <span className="text-sm text-gray-700 truncate flex-1">{item.name}</span>
              <span className="text-sm font-medium text-gray-500">{percentage}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// HORIZONTAL BAR CHART WITH LOGOS
// ==========================================

function HorizontalBarChartWithLogos({ data, palette, unit }) {
  const colors = CHART_PALETTES[palette] || CHART_PALETTES.blue;
  const maxValue = Math.max(...data.map(d => d.value), 1);

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={item.id} className="flex items-center gap-3">
          <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center">
            <ModelLogo modelName={item.fullName || item.id} size="sm" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 truncate">{item.name}</span>
                <span className="text-sm font-bold" style={{ color: colors[0] }}>
                    {item.value.toLocaleString()}{unit}
                 </span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: colors[Math.min(index, colors.length - 1)]
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==========================================
// FEATURE USAGE PIE (Chat / Compare / Tone)
// ==========================================

function FeatureUsagePie({ data, colors }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const featureIcons = { 'AI Chat': '💬', 'AI Compare': '⚔️', 'AI Tone': '🎭' };
  const featureColors = { 'AI Chat': '#3B82F6', 'AI Compare': '#8B5CF6', 'AI Tone': '#F43F5E' };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = ((item.value / total) * 100).toFixed(1);
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2">
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-600">{item.value} ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 h-full">
      <div className="w-full sm:w-1/2 h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              paddingAngle={3}
              dataKey="value"
              stroke="white"
              strokeWidth={2}
            >
              {data.map((entry, index) => (
                <Cell key={entry.id} fill={featureColors[entry.name] || colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="w-full sm:w-1/2 space-y-2">
        {data.map((item) => {
          const pct = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          const color = featureColors[item.name] || '#6B7280';
          return (
            <div key={item.id} className="flex items-center gap-3">
              <span className="text-lg">{featureIcons[item.name] || '📊'}</span>
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="text-sm text-gray-700 flex-1">{item.name}</span>
              <span className="text-sm font-bold" style={{ color }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// STAT CARD
// ==========================================

function StatCard({ icon, label, value, color }) {
  const colorStyles = {
    blue: { text: '#3B82F6', bg: 'bg-blue-50', border: 'border-blue-200' },
    purple: { text: '#8B5CF6', bg: 'bg-purple-50', border: 'border-purple-200' },
    green: { text: '#10B981', bg: 'bg-green-50', border: 'border-green-200' },
    amber: { text: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200' },
    rose: { text: '#F43F5E', bg: 'bg-rose-50', border: 'border-rose-200' },
  };

  const style = colorStyles[color] || colorStyles.blue;
  const IconComponent = Icons[icon] || Icons.Stats;

  return (
    <div
      className={`flex items-center gap-3 px-5 py-3 bg-white rounded-xl border ${style.border} shadow-sm hover:shadow-md transition-shadow min-w-[140px]`}
      title={`${label}: ${typeof value === 'number' ? value.toLocaleString() : value}`}
    >
      <div className={`p-2.5 rounded-lg ${style.bg}`}>
        {IconComponent && <IconComponent size={22} color={style.text} />}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-800">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
}

// ==========================================
// MODEL PAIRS CHART
// ==========================================

function ModelPairsChart({ data }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="space-y-3">
      {data.map((pair, index) => (
        <div
          key={pair.id}
          className="flex items-center gap-3 relative"
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <div className="flex items-center -space-x-2 flex-shrink-0">
            <div className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center z-10">
              <ModelLogo modelName={pair.model1} size="xs" />
            </div>
            <div className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center">
              <ModelLogo modelName={pair.model2} size="xs" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 truncate" title={pair.name}>
                {formatModelName(pair.model1)} <span className="text-gray-400">vs</span> {formatModelName(pair.model2)}
              </span>
              <span className="text-sm font-medium text-gray-500">{pair.value}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(pair.value / maxValue) * 100}%`,
                  backgroundColor: CHART_PALETTES.rose[Math.min(index, 4)]
                }}
              />
            </div>
          </div>

          {hoveredIndex === index && (
            <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-1.5 text-xs z-20 whitespace-nowrap">
              <span className="font-medium">{pair.value} comparisons</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ==========================================
// CHART CARD
// ==========================================

function ChartCard({ title, subtitle, color, children }) {
  const borderColors = {
    purple: 'border-l-purple-400',
    blue: 'border-l-blue-400',
    green: 'border-l-green-400',
    amber: 'border-l-amber-400',
    rose: 'border-l-rose-400',
  };

  return (
    <section
      className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 border-l-4 ${borderColors[color] || borderColors.blue}`}
      aria-labelledby={`chart-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="mb-4">
        <h2 id={`chart-${title.toLowerCase().replace(/\s/g, '-')}`} className="text-base font-semibold text-gray-900">
          {title}
        </h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className="min-h-[200px]">
        {children}
      </div>
    </section>
  );
}

function EmptyState({ message = "No data available" }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
      <svg className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ==========================================
// HELPERS
// ==========================================

function formatModelName(name) {
  if (!name) return 'Unknown';
  return name
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .substring(0, 20);
}

function formatCategoryName(category) {
  if (!category) return 'Other';
  return category
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default Dashboard;